import type { IndustryPage } from "@/lib/data";

export const electricians: IndustryPage = {
  slug: "electricians",
  seoTitle:
    "Websites for Electricians UK | Fixed £1,000, 7-Day Build",
  seoDescription:
    "Websites for electricians with EICR and EV charger pages, the NICEIC or NAPIT badges you hold and local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for electricians",
  tldr:
    "ConstructionWebDesign.uk builds websites for electricians across the UK: mobile-first sites with EICR, rewire and EV charger pages, one-tap calling and the NICEIC or NAPIT badges you hold. Each site costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Electrician website design · UK",
    headline: "Website design for electricians, from EICRs to EV chargers.",
    highlight: "EICRs to EV chargers",
    sub: "Websites for UK electricians with one-tap calling, a page for every service from EICRs to EV chargers, a quick quote form, the NICEIC, NAPIT or Part P details you hold, a commercial section for facilities buyers and on-page local SEO.",
    secondaryCta: { label: "See electrician demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Electricians.jpg",
      alt: "Electrician working on a domestic consumer unit and rewire",
      headlineTop: "Win regular electrical",
      headlineBottom: "work in your patch.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/voltline.jpg",
    },
  ],
  demoCategory: "Electricians",
  demosHeading: "Electrician website demo templates.",
  demosSub:
    "Demo templates for domestic, commercial and EV-install electricians. Open one to see the layout, pages and features an electrician website can include. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does an electrician need a website?",
    intro:
      "An electrician needs a website because it gives a homeowner, landlord or facilities manager one place to see your services, the areas you cover, your reviews and the registrations you hold, then ring you in one tap. An electrician website keeps taking enquiries while you're on a job.",
    points: [
      {
        title: "Look qualified and local from the first visit.",
        body: "A clear, mobile-first electrician website with your services, the areas you cover and photos of your own installs shows a homeowner you're an established business before they pick up the phone.",
        icon: "Bolt",
      },
      {
        title: "Turn visits into callouts, not clicks.",
        body: "Click-to-call buttons, a sticky number bar on mobile and a short enquiry form give every visitor an easy way to reach you, on every page, instead of a dead-end contact page.",
        icon: "Phone",
      },
      {
        title: "Get found for \"electrician near me\".",
        body: "On-page local SEO, a page for each area you cover and a link to your Google Business Profile help Google match your website to electrical searches in your patch.",
        icon: "Pin",
      },
      {
        title: "Show the registrations homeowners check.",
        body: "NICEIC, NAPIT, Part P, 18th Edition and your insurance details sit in the header, with registration numbers linked to the official register. That is the reassurance a homeowner wants before letting an electrician into the house. Only what you hold goes on the site.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should an electrician website include?",
    intro:
      "An electrician website should include a one-tap call button, a page for each service, the registrations you hold, your reviews, the areas you cover and a quick quote form. Electricians who do commercial work also need a separate section for facilities buyers.",
    items: [
      {
        title: "Emergency-led homepage with one-tap call.",
        body: "The first thing a visitor sees on a phone is your number, not a slideshow. A sticky call bar stays on screen as they scroll, so someone with no power or a tripping RCD can ring you in one tap. If you take emergency callouts, the homepage says so up front.",
      },
      {
        title: "A page for every electrical service.",
        body: "EICRs (Electrical Installation Condition Reports), consumer unit and fuse board upgrades, full and partial rewires, fault finding, PAT testing, EV charger installation and smart-home work each get their own page. Each page targets its own search, so a landlord looking for an EICR lands on your EICR page.",
      },
      {
        title: "Example prices and a quick quote form.",
        body: "Publish typical price ranges for common jobs such as an EICR, a rewire, a fuse board or an EV charger, if you want to. The quote form asks for a postcode, a photo and a short description, so you can price the job before you ring back.",
      },
      {
        title: "NICEIC, NAPIT and Part P badges in the header.",
        body: "If you're registered with NICEIC, NAPIT or ELECSA, qualified to the 18th Edition (BS 7671) or TrustMark registered, the badges sit in the header with registration numbers linked to the official register. Scheme membership shows you can self-certify notifiable work under Part P of the Building Regulations in England and Wales. Anything you don't hold stays off the site.",
      },
      {
        title: "Commercial section for facilities buyers.",
        body: "A separate area for commercial and industrial work: periodic testing schedules, planned maintenance, emergency lighting and fire alarm testing, and multi-site quoting. Same site, a different route for a facilities manager than for a homeowner.",
      },
      {
        title: "Local SEO built in from day one.",
        body: "Every electrician website includes on-page local SEO: schema markup, location pages for the towns you cover, postcode and town keywords in page titles and a link to your Google Business Profile.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does an electrician website cost in the UK?",
      a: "An electrician website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. You own the website.",
    },
    {
      q: "How long does it take to build an electrician website?",
      a: "Electrician websites from ConstructionWebDesign.uk go live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you explain the work you want more of, such as EICRs, rewires or EV chargers, and the areas you cover. For comparison, a typical UK agency project takes 8–12 weeks.",
    },
    {
      q: "What pages should an electrician's website have?",
      a: "An electrician's website should have a homepage with a one-tap call button, a page for each service (EICRs, consumer unit upgrades, rewires, fault finding, PAT testing, EV chargers), pages for the areas you cover, your reviews and a contact page with a short quote form. Electricians who do commercial work also need a separate section for facilities buyers. ConstructionWebDesign.uk agrees the sitemap with you before design starts.",
    },
    {
      q: "Do electricians need a website?",
      a: "Yes, electricians need a website, even as a sole trader. A website gives a homeowner, landlord or facilities manager who finds you on Google, Checkatrade or the side of your van somewhere to check your services, reviews and accreditations before they ring. Directory profiles list you next to other electricians. Your own website shows only your business, takes enquiries while you're on a job and belongs to you.",
    },
    {
      q: "Can I show my NICEIC or NAPIT registration on my website?",
      a: "Yes, an electrician can show NICEIC, NAPIT or ELECSA registration on the website, and it belongs near the top of the page. Registration with one of these competent person schemes lets you self-certify notifiable work under Part P of the Building Regulations in England and Wales. ConstructionWebDesign.uk links each badge to the official register so customers can check it, and leaves off any badge you don't hold.",
    },
    {
      q: "How do electricians get found on Google?",
      a: "Electricians get found on Google through a website with a page for each service and area, on-page local SEO and a complete Google Business Profile with regular reviews. Electrician website design from ConstructionWebDesign.uk includes on-page local SEO as standard: schema markup, location pages, postcode keywords and a link to your Google Business Profile. Nobody can honestly guarantee a ranking, but these are the basics an electrician needs in place.",
    },
    {
      q: "Can one electrician website cover domestic and commercial work?",
      a: "Yes, one electrician website can cover domestic and commercial work, with a separate section for each. Homeowners want rewires, fuse boards, EV chargers and a quick price. Facilities managers want testing schedules, planned maintenance and multi-site quotes. Splitting the two gives each buyer its own pages and its own route to an enquiry, on the same site.",
    },
    {
      q: "Can I build my own electrician website on Wix or Squarespace?",
      a: "Yes, an electrician can build a website on Wix, Squarespace or WordPress, and Wix or Squarespace typically cost about £22 a month. The trade-off is time: you write the service pages, set up local SEO and add your NICEIC or NAPIT details yourself, and a Wix or Squarespace site goes offline if you stop paying. Web design for electricians from ConstructionWebDesign.uk costs £1,000 fixed, and you own the website.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
    {
      href: "/blog/how-to-win-subcontractor-work",
      label: "How to win subcontractor work and get on tender lists",
    },
  ],
  finalCta: {
    headline: "Plan your electrician website on a free call.",
    highlight: "electrician website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
